const STORAGE_VERSION = 1 as const;

export type TrainerTourState = {
  version: typeof STORAGE_VERSION;
  /** Visite entièrement désactivée (croix sur le tableau de bord). */
  dismissed: boolean;
  /** Étapes du tableau de bord terminées (ou passées). */
  dashboardWalkthroughDone: boolean;
  /** Clés de pages pour lesquelles l’info contextuelle a déjà été affichée. */
  routeHintsShown: string[];
};

const defaultState = (): TrainerTourState => ({
  version: STORAGE_VERSION,
  dismissed: false,
  dashboardWalkthroughDone: false,
  routeHintsShown: [],
});

function storageKey(userId: string): string {
  return `lyrra:trainer-tour:v${STORAGE_VERSION}:${userId}`;
}

export function loadTrainerTourState(userId: string | undefined): TrainerTourState {
  if (!userId || typeof window === 'undefined') return defaultState();
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw) as Partial<TrainerTourState>;
    if (parsed.version !== STORAGE_VERSION) return defaultState();
    return {
      version: STORAGE_VERSION,
      dismissed: Boolean(parsed.dismissed),
      dashboardWalkthroughDone: Boolean(parsed.dashboardWalkthroughDone),
      routeHintsShown: Array.isArray(parsed.routeHintsShown)
        ? parsed.routeHintsShown.filter((x) => typeof x === 'string')
        : [],
    };
  } catch {
    return defaultState();
  }
}

export function saveTrainerTourState(userId: string | undefined, state: TrainerTourState): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(state));
  } catch {
    // ignore quota / private mode
  }
}

export function markTrainerTourDismissed(userId: string | undefined): TrainerTourState {
  const prev = loadTrainerTourState(userId);
  const next: TrainerTourState = { ...prev, dismissed: true };
  saveTrainerTourState(userId, next);
  return next;
}

export function markTrainerTourDashboardDone(userId: string | undefined): TrainerTourState {
  const prev = loadTrainerTourState(userId);
  const next: TrainerTourState = { ...prev, dashboardWalkthroughDone: true };
  saveTrainerTourState(userId, next);
  return next;
}

export function markTrainerTourRouteShown(userId: string | undefined, routeKey: string): TrainerTourState {
  const prev = loadTrainerTourState(userId);
  if (prev.routeHintsShown.includes(routeKey)) return prev;
  const next: TrainerTourState = {
    ...prev,
    routeHintsShown: [...prev.routeHintsShown, routeKey],
  };
  saveTrainerTourState(userId, next);
  return next;
}
